import { useSuspenseQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query'
import { createQueryKey, FINANCIAL_MODEL_SETTINGS_BASE_KEY, OPEX_BASE_KEY, TOTALS_KEY } from '@/api/query-keys.ts'
import { axiosInstance } from '@/api/axios.ts'
import {
  CreateCustomerOpexCategoryResponse,
  CreateCustomOpexCategoryRequest,
  DeleteOpexSelectedCategoryRequest,
  GetOpexCategoriesRequest,
  GetOpexCategoriesResponse,
  GetOpexDataEntriesRequest,
  GetOpexDataEntriesResponse,
  GetOpexSelectedCategoriesRequest,
  GetOpexSelectedCategoriesResponse,
  OpexCategory,
  OpexCategoryTotalWithChildren,
  OpexTotalsResponse,
  UpdateOpexColumnConfigRequest,
  UpdateOpexSelectedCategoriesRequest,
  UpsertOpexDataEntriesRequest,
  UpsertOpexDataEntriesResponse
} from './types.ts'
import { ModelingApproach, PeriodTypeView } from '@/types'

export const createOpexQueryKeys = {
  categories: ({ financialModelId }: { financialModelId: string }) =>
    createQueryKey({
      baseKey: OPEX_BASE_KEY,
      keys: [`categories`, financialModelId]
    }),
  selectedCategories: ({ financialModelId }: { financialModelId: string }) =>
    createQueryKey({
      baseKey: OPEX_BASE_KEY,
      keys: [`categories`, financialModelId, `selected`]
    }),
  getOpexDataEntriesByCategoryId: ({ opexCategoryId, financialModelId, view, queryPeriod }: Partial<GetOpexDataEntriesRequest>) =>
    createQueryKey({
      baseKey: OPEX_BASE_KEY,
      keys: [`categories`, financialModelId, `periods`, opexCategoryId, view, queryPeriod]
    }),
  columnNameConfig: ({ financial_model_id }: Pick<UpdateOpexColumnConfigRequest, 'financial_model_id'>) =>
    createQueryKey({
      baseKey: OPEX_BASE_KEY,
      keys: [`switch-opex-column-type`, financial_model_id, `settings`, `columns`]
    }),
  copyPasteRow: ({
    financialModelId,
    modelingApproach,
    opexCategoryId
  }: {
    financialModelId: string
    modelingApproach: ModelingApproach
    opexCategoryId: string
  }) =>
    createQueryKey({
      baseKey: OPEX_BASE_KEY,
      keys: [`copypaste`, `historical`, `monthly`, `categories`, financialModelId, `periods`, opexCategoryId, modelingApproach]
    }),
  getTotals: ({ financialModelId, view }: { financialModelId: string; view: PeriodTypeView }) =>
    createQueryKey({
      baseKey: TOTALS_KEY,
      keys: OPEX_BASE_KEY.concat([financialModelId, view])
    })
}

// ***************************************************************************** //
// *********************************** SETUP *********************************** //
// ***************************************************************************** //

// **** GET -> CATEGORIES **** //

export const getOpexCategories = async ({ financialModelId }: GetOpexCategoriesRequest) => {
  const { data } = await axiosInstance.get<GetOpexCategoriesResponse>(`/financial-models/${financialModelId}/opex-categories`)
  return data
}

export const useGetOpexCategories = ({ financialModelId }: GetOpexCategoriesRequest) =>
  useSuspenseQuery({
    queryKey: createOpexQueryKeys.categories({ financialModelId }),
    queryFn: () => getOpexCategories({ financialModelId }),
    select: data =>
      data.map(category => ({
        ...category,
        type: category.category_type
      }))
  })

// GET -> SELECTED CATEGORIES

export const getSelectedOpexCategories = async ({ financialModelId }: GetOpexSelectedCategoriesRequest) => {
  const { data } = await axiosInstance.get<GetOpexSelectedCategoriesResponse>(`/financial-models/${financialModelId}/selected-opex-categories`)
  return data
}

export const useGetOpexSelectedCategories = ({ financialModelId }: GetOpexSelectedCategoriesRequest) =>
  useSuspenseQuery({
    queryKey: createOpexQueryKeys.selectedCategories({ financialModelId }),
    queryFn: () => getSelectedOpexCategories({ financialModelId }),
    select: data =>
      data
        .map(category => ({
          ...category,
          type: category.opex_category.category_type
        }))
        .sort((a, b) => a.opex_category.name.localeCompare(b.opex_category.name))
  })

// **** CREATE -> CATEGORIES **** //

export const createOpexCategory = async ({ financialModelId, ...body }: CreateCustomOpexCategoryRequest) => {
  const { data } = await axiosInstance.post<CreateCustomerOpexCategoryResponse>(`/financial-models/${financialModelId}/opex-categories`, body)
  return data
}

export const useCreateOpexCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateCustomOpexCategoryRequest) => createOpexCategory(request),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.categories({
          financialModelId
        })
      })
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.selectedCategories({
          financialModelId
        })
      })
    }
  })
}

// **** UPDATE -> SELECTED CATEGORIES **** //
const updateOpexSelectedCategories = async ({ financialModelId, ...item }: UpdateOpexSelectedCategoriesRequest) => {
  const { data } = await axiosInstance.put<OpexCategory[]>(`/financial-models/${financialModelId}/selected-opex-categories`, [item])
  return data
}

export const useUpdateOpexSelectedCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdateOpexSelectedCategoriesRequest) => updateOpexSelectedCategories(request),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.categories({ financialModelId })
      })
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.selectedCategories({ financialModelId })
      })
    }
  })
}

// **** DELETE - CATEGORY **** //

export const deleteOpexCategory = async ({ financialModelId, categoryId }: DeleteOpexSelectedCategoryRequest) => {
  await axiosInstance.delete(`/financial-models/${financialModelId}/opex-categories/${categoryId}`)
}

export const useDeleteOpexCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: DeleteOpexSelectedCategoryRequest) => deleteOpexCategory(request),
    onSuccess: (_, { financialModelId }) => {
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.categories({ financialModelId })
      })
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.selectedCategories({ financialModelId })
      })
    }
  })
}

// **************************************************************************** //
// ******************************* DATA-ENTRIES ******************************* //
// **************************************************************************** //

// **** GET -> DATA ENTRIES BY CATEGORY ID **** //

const getOpexDataEntriesByCategoryId = async ({ financialModelId, opexCategoryId, queryPeriod, view }: GetOpexDataEntriesRequest) => {
  const { data } = await axiosInstance.get<GetOpexDataEntriesResponse>(`/financial-models/${financialModelId}/opex/data-entries/${opexCategoryId}`, {
    params: {
      data_type: queryPeriod,
      view
    }
  })
  return data
}

export const useGetOpexDataEntriesByCategoryId = ({ financialModelId, opexCategoryId, queryPeriod, view }: GetOpexDataEntriesRequest) =>
  useSuspenseQuery({
    queryKey: createOpexQueryKeys.getOpexDataEntriesByCategoryId({
      opexCategoryId,
      financialModelId,
      view,
      queryPeriod
    }),
    queryFn: () =>
      getOpexDataEntriesByCategoryId({
        financialModelId,
        opexCategoryId,
        queryPeriod,
        view
      }),
    select: data => ({
      ...data,
      periods: data.periods.map(period => ({
        ...period,
        period_type: period.type
      }))
    })
  })

// POST -> UPSERT DATA ENTRIES BY CATEGORY ID

export const upsertOpexDataEntriesByCategoryId = async ({ financialModelId, data: body }: UpsertOpexDataEntriesRequest) => {
  const { data } = await axiosInstance.post<UpsertOpexDataEntriesResponse>(`/financial-models/${financialModelId}/opex/data-entries`, body)
  return data
}

export const getUpsertOpexDataEntriesByCategoryIdMutationArgs = (queryClient: QueryClient) => ({
  mutationFn: (request: UpsertOpexDataEntriesRequest) => upsertOpexDataEntriesByCategoryId(request),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: OPEX_BASE_KEY
    })
  }
})

export const useUpsertOpexDataEntriesByCategoryId = () => {
  const queryClient = useQueryClient()
  return useMutation(getUpsertOpexDataEntriesByCategoryIdMutationArgs(queryClient))
}

// POST -> DATA ENTRIES BY CATEGORY ID SETTINGS

const updateOpexColumnConfig = ({ financial_model_id, ...params }: UpdateOpexColumnConfigRequest) => {
  const { column_name, ...rest } = params
  return axiosInstance.post(`/financial-models/${financial_model_id}/opex/config/${column_name}`, undefined, {
    params: rest
  })
}

export const useUpdateOpexColumnConfig = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdateOpexColumnConfigRequest) => updateOpexColumnConfig(request),
    mutationKey: createOpexQueryKeys.columnNameConfig({
      financial_model_id: financialModelId
    }),
    onSuccess: (_, { opex_category_id }) => {
      queryClient.invalidateQueries({
        queryKey: createOpexQueryKeys.getOpexDataEntriesByCategoryId({
          opexCategoryId: opex_category_id,
          financialModelId
        })
      })
    }
  })
}

// GET -> OPEX TOTALS

const getOpexTotals = async ({ financialModelId, view }: { financialModelId: string; view: PeriodTypeView }) => {
  const { data } = await axiosInstance.get<OpexTotalsResponse>(`/financial-models/${financialModelId}/opex/total-results`, { params: { view } })
  return data
}

export const useGetSuspenseOpexTotals = ({ financialModelId, view }: { financialModelId: string; view: PeriodTypeView }) =>
  useSuspenseQuery({
    queryKey: createOpexQueryKeys.getTotals({ financialModelId, view }),
    queryFn: () => getOpexTotals({ financialModelId, view }),
    select: data => {
      const onlyCategories = data.totals.by_category
        .filter(cat => !cat.parent_category_id)
        .sort((a, b) => a.category_name.localeCompare(b.category_name))

      const groupedByCategory = onlyCategories.reduce(
        (acc, curr) => {
          if (!acc[curr.category_id]) {
            acc[curr.category_id] = {
              ...curr,
              children: []
            }
          }
          return acc
        },
        {} as Record<string, OpexCategoryTotalWithChildren>
      )

      const allCategories = [...data.totals.by_category]

      for (const category of allCategories) {
        if (category.parent_category_id) {
          if (groupedByCategory[category.parent_category_id]) {
            groupedByCategory[category.parent_category_id].children = [
              ...(groupedByCategory[category.parent_category_id].children || []),
              {
                ...category,
                children: []
              }
            ]
          }
        }
      }

      const children = Object.values(groupedByCategory).flatMap(category => category.children || [])

      for (const child of children) {
        for (const category of allCategories) {
          if (child.category_id === category.parent_category_id) {
            const parent = groupedByCategory[child!.parent_category_id!]
            if (parent) {
              const selectedChild = parent.children?.find(c => c.category_id === child.category_id)
              if (selectedChild) {
                selectedChild.children = [...(selectedChild.children || []), category]
              }
            }
          }
        }
      }

      return {
        ...data,
        totals: {
          ...data.totals,
          by_category: {
            categories: onlyCategories,
            cxgr: [...onlyCategories.map(cat => cat.cxgr), data.totals.total.cxgr]
          },
          groupedByCategory: Object.values(groupedByCategory)
        }
      }
    }
  })
